import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Icon from '../components/ui/Icon.jsx';
import SectionState from '../components/ui/SectionState.jsx';
import Pagination from '../components/ui/Pagination.jsx';
import { useApi } from '../hooks/useApi.js';
import { getThemes, getTheme, getThemeCounts } from '../api/themesApi.js';
import {
  THEME_TYPES,
  THEME_FALLBACK_BG,
  resolveThemeStatus,
  DEFAULT_AVAILABLE_NOTE,
  DEFAULT_ENDED_NOTE,
} from '../data/themes.js';
import { analyticsEvent, trackFilter, trackSearch } from '../utils/analytics.js';
import './GalleryPage.css';

// YYYY-MM-DD → YYYY/MM/DD（顯示用）；空值回 ''
function fmtDate(iso) {
  return iso ? String(iso).replace(/-/g, '/') : '';
}

// 是否尊重「減少動態」偏好（前庭敏感者不自動播放 hover 預覽）
function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * 解析影片網址，產生兩種嵌入網址：
 *   preview = 靜音自動播放、無控制列、loop（卡片 hover 預覽用，瀏覽器允許靜音自動播）
 *   full    = 有聲音、可控制（詳情頁用，使用者點播放=合法手勢，可出聲）
 * 支援 YouTube / Vimeo / 直連檔案（.mp4 等）。
 */
function parseVideo(url) {
  if (!url) return null;
  const u = url.trim();

  let m = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (m) {
    const id = m[1];
    return {
      provider: 'youtube',
      preview: `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&loop=1&playlist=${id}&modestbranding=1&playsinline=1&rel=0`,
      full: `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`,
    };
  }

  m = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m) {
    const id = m[1];
    return {
      provider: 'vimeo',
      preview: `https://player.vimeo.com/video/${id}?autoplay=1&muted=1&loop=1&background=1`,
      full: `https://player.vimeo.com/video/${id}`,
    };
  }

  if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(u)) {
    return { provider: 'file', src: u };
  }

  return null;
}

/**
 * 把純文字裡的網址轉成可點連結（linkify）。
 * 只認 http/https，擋掉 javascript: 等危險 scheme；全程用 React 元素渲染，
 * 不用 dangerouslySetInnerHTML，所以沒有 XSS 風險。換行交給 CSS white-space: pre-line 保留。
 */
function Linkify({ text }) {
  if (!text) return null;
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer">{part}</a>
      : <span key={i}>{part}</span>
  );
}

// 主題縮圖：有圖片用圖片，否則用類型對應的漸層底
function ThemeThumb({ t }) {
  const img = t.images && t.images[0];
  if (img) {
    return <div className="tg-thumb"><img src={img} alt={t.name} loading="lazy" /></div>;
  }
  return (
    <div className="tg-thumb" style={{ background: THEME_FALLBACK_BG[t.type] || '#222' }}>
      <div className="tg-scan" />
      <span className="tg-name-on">{t.name}</span>
    </div>
  );
}

function ThemeCard({ t }) {
  const ty = THEME_TYPES[t.type];
  const [hover, setHover] = useState(false);
  const video = parseVideo(t.videoUrl);
  const showPreview = hover && video && !prefersReducedMotion();
  const st = resolveThemeStatus(t);

  return (
    <Link
      className="tg-card"
      to={`/gallery/${t.id}`}
      onPointerDown={() => analyticsEvent('select_content', { content_type: 'theme', content_id: String(t.id), item_name: t.name, theme_type: t.type })}
      onClick={() => window.scrollTo(0, 0)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className="tg-preview">
        <ThemeThumb t={t} />
        {showPreview && (video.provider === 'file'
          ? <video className="tg-video" src={video.src} loop playsInline autoPlay ref={el => { if (el) el.muted = true; }} />
          : <iframe className="tg-embed" src={video.preview} title={t.name} allow="autoplay; encrypted-media" frameBorder="0" />
        )}
        {ty && <span className="tg-type-badge">{ty.short}</span>}
        <span className={`tg-src${st.isEnded ? ' ended' : ''}`}>{st.badge}</span>
        {video && !hover && <span className="tg-play" />}
      </div>
      <h3>{t.name}</h3>
      <div className="tg-foot">
        <span className="tg-type-label">{ty?.label}</span>
        <span className="tg-source">{t.source}</span>
      </div>
    </Link>
  );
}

const RELATED_COUNT = 4;        // 同類型主題顯示筆數
const RELATED_TYPE_LIMIT = 200; // 抓同 type 清單的上限（單一 type 不會超過此數）

// 從「同 type、依日期排序」的清單取當前主題的前後鄰居；位於頭尾時往另一側補滿
function pickNeighbors(list, currentId, count) {
  const idx = list.findIndex(x => x.id === currentId);
  if (idx === -1) return list.filter(x => x.id !== currentId).slice(0, count);
  const windowSize = count + 1; // 含自己
  let start = Math.min(idx - Math.floor(count / 2), list.length - windowSize);
  start = Math.max(0, start);
  return list.slice(start, start + windowSize).filter(x => x.id !== currentId);
}

// 詳情
function ThemeDetail({ id }) {
  const { data: t, loading } = useApi(() => getTheme(id), [id]);
  // 同類型主題：抓同 type 全部（依取得日期排序），於前端取當前主題的前後鄰居
  const { data: relatedRes } = useApi(
    () => (t?.type ? getThemes({ type: t.type, limit: RELATED_TYPE_LIMIT, sort: 'acquired_desc' }) : Promise.resolve({ items: [] })),
    [t?.type, id]
  );

  useEffect(() => {
    if (!t) return;
    analyticsEvent('view_theme', {
      content_type: 'theme', content_id: String(t.id), item_name: t.name, theme_type: t.type, source: t.source,
    });
  }, [t]);

  if (loading) return <main className="tg"><div className="tg-loading">載入中…</div></main>;
  if (!t) {
    return <main className="tg"><p className="tg-loading">找不到此主題。<Link to="/gallery">返回主題圖鑑</Link></p></main>;
  }

  const ty = THEME_TYPES[t.type];
  const video = parseVideo(t.videoUrl);
  const related = pickNeighbors(relatedRes?.items || [], t.id, RELATED_COUNT);
  const st = resolveThemeStatus(t);
  const period = st.from || st.until
    ? `${fmtDate(st.from) || '—'} ～ ${fmtDate(st.until) || '不限'}`
    : '';

  return (
    <main className="td">
      <Link className="td-back" to="/gallery" onClick={() => window.scrollTo(0, 0)}><Icon name="arrow-l" size={14} /> 返回主題圖鑑</Link>
      <div className="td-grid">
        <div>
          <div className="td-media">
            <div className={`td-screen${video ? ' has-video' : ''}`}>
              {video ? (
                video.provider === 'file'
                  ? <video className="td-video-el" src={video.src} controls playsInline poster={t.images?.[0]} />
                  : <iframe
                      className="td-embed"
                      src={video.full}
                      title={t.name}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
              ) : (
                <ThemeThumb t={t} />
              )}
            </div>
          </div>
        </div>

        <div className="td-info">
          {ty && <span className="tg-type-badge static">{ty.label}</span>}
          <h1>{t.name}</h1>

          <dl className="td-spec">
            <div className="r"><dt>道具類型</dt><dd>{ty?.label}</dd></div>
            <div className="r"><dt>取得來源</dt><dd className={st.isEnded ? 'ended' : ''}>{t.source}</dd></div>
            {t.acquiredDate && <div className="r"><dt>取得日期</dt><dd>{fmtDate(t.acquiredDate)}</dd></div>}
            {period && <div className="r"><dt>取得期間</dt><dd className={st.isEnded ? 'ended' : ''}>{period}</dd></div>}
          </dl>

          {t.story && (
            <div className="td-story-block">
              <h3>參考資料</h3>
              <p className="td-story"><Linkify text={t.story} /></p>
            </div>
          )}

          {st.state === 'ended' ? (
            <div className="td-status ended">
              <Icon name="lock" size={18} />
              <div>
                <b>此道具已絕版</b><br />
                {(t.endedNote || '').trim() || DEFAULT_ENDED_NOTE}
              </div>
            </div>
          ) : st.state === 'upcoming' ? (
            <div className="td-status active">
              <Icon name="bell" size={18} />
              <div>
                <b>尚未開放取得</b><br />
                {fmtDate(st.from)} 起開放取得{st.until ? `，至 ${fmtDate(st.until)} 止` : ''}。
              </div>
            </div>
          ) : (
            <div className="td-status active">
              <Icon name="check" size={18} />
              <div>
                <b>{st.limited ? '此道具限定期間可取得' : '此道具仍可取得'}</b><br />
                {st.limited && st.until && <>可取得至 {fmtDate(st.until)}。<br /></>}
                {(t.availableNote || '').trim() || DEFAULT_AVAILABLE_NOTE}
              </div>
            </div>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <section className="td-related">
          <h3>同類型主題</h3>
          <div className="tg-grid">
            {related.map(rt => <ThemeCard key={rt.id} t={rt} />)}
          </div>
        </section>
      )}
    </main>
  );
}

const PAGE_SIZE = 12;

const TYPE_TABS = [
  { key: 'all', label: '全部' },
  { key: 'd2', label: 'DARTSLIVE 2' },
  { key: 'd3', label: 'DARTSLIVE 3' },
  { key: 'fun', label: 'Fun Dart' },
  { key: 'award', label: 'Award Movie' },
];

// 列表
function GalleryPage() {
  const { id } = useParams();
  const [type, setType] = useState('all');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('acquired_desc'); // acquired_desc 新→舊（預設）/ acquired_asc 舊→新

  // 搜尋字串防抖，避免逐字打 API
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const h = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(h);
  }, [query]);

  useEffect(() => {
    trackSearch(debouncedQuery, 'theme_gallery');
  }, [debouncedQuery]);

  useEffect(() => {
    trackFilter('theme_type', type, 'theme_gallery');
    trackFilter('sort', sort, 'theme_gallery');
  }, [type, sort]);

  // 換分類 / 換關鍵字 / 換排序 → 回到第一頁
  useEffect(() => { setPage(1); }, [type, debouncedQuery, sort]);

  // 後端分頁：換分類 / 換關鍵字 / 換排序 / 換頁都重新 fetch
  const { data: res, loading, error, refetch } = useApi(
    () => getThemes({ type: type === 'all' ? undefined : type, q: debouncedQuery, page, limit: PAGE_SIZE, sort: sort || undefined }),
    [type, debouncedQuery, page, sort]
  );
  // 分類計數（tab 徽章）獨立取，不受搜尋 / 分頁影響
  const { data: counts = {} } = useApi(() => getThemeCounts(), []);

  const items = res?.items || [];
  const total = res?.total || 0;
  const totalPages = res?.totalPages || 1;

  const gridRef = useRef(null);
  // 換頁後捲到第一個主題卡（不回到最上面的標題）
  const goPage = (p) => { setPage(p); gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  // 有 id → 顯示詳情
  if (id) return <ThemeDetail id={id} />;

  // res 在 refetch 期間會保留前一次結果（useApi 不重設 data），所以換頁 / 換分類不會閃骨架
  const firstLoading = loading && !res;

  return (
    <main className="tg">
      <header className="tg-hero">
        <p className="tg-eyebrow">圖鑑</p>
        <h1>主題<em>圖鑑</em></h1>
        <p>收錄 DARTSLIVE 2 / DARTSLIVE 3 / Fun Dart / Award Movie 歷年主題。多數已絕版，此頁純為記錄與分享 — 點任一主題看取得來源。</p>
        <div className="tg-counts">
          <div className="tg-count"><b>{counts.all ?? 0}</b><span>THEMES</span></div>
          <div className="tg-count"><b>4</b><span>TYPES</span></div>
        </div>
      </header>

      <div className="tg-tabs" role="tablist">
        {TYPE_TABS.map(tab => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={type === tab.key}
            className={`tg-tab${type === tab.key ? ' on' : ''}`}
            onClick={() => setType(tab.key)}
          >
            {tab.label}
            <span className="n">{counts[tab.key] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="tg-toolbar">
        <div className="tg-search">
          <Icon name="search" size={18} />
          <input
            type="search"
            placeholder="搜尋主題 · 名稱 / 來源 / 關鍵字"
            value={query}
            onChange={e => setQuery(e.target.value)}
            aria-label="搜尋主題"
          />
          {query && (
            <button type="button" className="tg-search-clear" onClick={() => setQuery('')} aria-label="清除搜尋">
              <Icon name="x" size={14} />
            </button>
          )}
        </div>
        <select
          className="tg-sort"
          value={sort}
          onChange={e => setSort(e.target.value)}
          aria-label="排序"
          style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line, #d8dce6)', font: '600 13px "Noto Sans TC", sans-serif', color: 'var(--navy, #0e1c40)', background: '#fff', cursor: 'pointer' }}
        >
          <option value="acquired_desc">取得日期：新→舊</option>
          <option value="acquired_asc">取得日期：舊→新</option>
        </select>
      </div>

      {error ? (
        <SectionState error={error} onRetry={refetch} />
      ) : firstLoading ? (
        <div className="tg-grid">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="tg-card-skeleton" />)}
        </div>
      ) : items.length === 0 ? (
        <SectionState emptyText={debouncedQuery ? `找不到符合「${debouncedQuery}」的主題。` : '此分類目前沒有主題。'} />
      ) : (
        <>
          <div className="tg-grid" ref={gridRef} style={{ scrollMarginTop: 90 }}>
            {items.map(t => <ThemeCard key={t.id} t={t} />)}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={goPage} />
        </>
      )}
    </main>
  );
}

export default GalleryPage;
